import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../../components/client/PhoneInput.css';
import ClientLayout from '../../layouts/ClientLayout';
import { authService } from '../../lib/api/apiService';
import { useCart } from '../../context/CartContext';
import { addressService } from '../../lib/api/addressService';
import { cartAPI, ordersAPI } from '../../api/endpoints';
import { promoService } from '../../lib/api/shopService';
import useCartSync from '../../hooks/useCartSync';
import { CART_QUERY_ROOT, getCartQueryKey } from '../../hooks/useCartQuery';
import useCartOperations from '../../hooks/useCartOperations';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useTranslation } from 'react-i18next';
import { useKKiaPay } from 'kkiapay-react';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import { kkiapayPublicKey, kkiapaySandbox } from '../../config/kkiapay';
import { fedapayPublicKey } from '../../config/fedapay';
import { isFedaPayProvider } from '../../config/paymentProvider';
import { openFedaPayCheckout } from '../../utils/fedapayCheckout';
import { getRecaptchaToken, preloadRecaptcha } from '../../utils/recaptcha';
import RecaptchaLegalNotice from '../../components/RecaptchaLegalNotice';
import { computeCheckoutTotal } from '../../utils/checkoutTotals';
import {
  extractKkiapayAccount,
  extractKkiapayChannel,
} from '../../utils/kkiapayPayload';
import { persistCheckoutAuthSession } from '../../utils/orderAuth';
import { unwrapOrderResponse } from '../../lib/api/schemas/orders';

// Composant Modal pour le choix d'adresse de livraison
const DeliveryModeModal = ({
  isOpen,
  onClose,
  options,
  selectedId,
  onSelect,
  onAddAddress,
  isAdding,
  isFetchingLocation,
  newAddressName,
  onNewAddressNameChange,
  onCreateAddress,
  isSavingAddress,
  onFinalize,
  locationError,
  addressSaveError,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('cart.delivery_modal.title')} </h2>
          <button
            onClick={onClose}
            className="text-gray-400 transition hover:text-gray-600"
            aria-label={t('cart.delivery_modal.close')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {options.map((option) => {
            const isActive = option.id === selectedId;
            return (
              <label
                key={option.id}
                className={`flex cursor-pointer flex-col rounded-xl border px-4 py-3 text-sm transition hover:border-orange-500 ${isActive ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="delivery-mode"
                    value={option.id}
                    checked={isActive}
                    onChange={() => onSelect(option.id)}
                    className="mt-1 h-4 w-4 border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <div className="flex-1 space-y-1">
                    <p className="font-medium text-gray-900">{option.label}</p>
                    {option.address && (
                      <p className="text-gray-600">{option.address}</p>
                    )}
                    {typeof option.cost === 'number' && (
                      <span className={`text-xs font-semibold ${option.cost === 0 ? 'text-green-600' : 'text-orange-500'}`}>
                        {option.cost === 0
                          ? t('cart.delivery_modal.free_shipping')
                          : `+ ${option.cost.toLocaleString()} FCFA`}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            );
          })}

          {isAdding && (
            <div className="rounded-xl border border-dashed border-orange-300 bg-orange-50 px-4 py-3 text-sm text-gray-700">
              {isFetchingLocation ? (
                <p>{t('cart.delivery_modal.locating')}</p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('cart.delivery_modal.place_name')}
                    </label>
                    <input
                      type="text"
                      value={newAddressName}
                      onChange={(e) => onNewAddressNameChange(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
                      placeholder={t('cart.delivery_modal.place_name_placeholder')}
                    />
                  </div>

                  {(locationError || addressSaveError) && (
                    <p className="text-xs text-red-500">
                      {addressSaveError || locationError}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={onCreateAddress}
                    disabled={
                      isSavingAddress ||
                      isFetchingLocation ||
                      !newAddressName.trim()
                    }
                    className={`w-[fit-content] rounded-full px-4 py-1 text-sm font-medium text-white transition ${isSavingAddress || isFetchingLocation || !newAddressName.trim()
                        ? 'cursor-not-allowed bg-gray-400'
                        : 'bg-blue-700 hover:bg-blue-800'
                      }`}
                  >
                    {isSavingAddress ? t('cart.delivery_modal.adding') : t('cart.delivery_modal.add')}
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={onAddAddress}
            className="text-left text-sm font-medium text-orange-500 transition hover:text-orange-600"
          >
            {t('cart.delivery_modal.add_address')}
          </button>
        </div>

        <div className="rounded-b-2xl border-t px-6 py-4">
          <button
            onClick={onFinalize}
            disabled={isFetchingLocation || isSavingAddress}
            className={`w-full rounded-full py-2.5 text-sm font-semibold text-white transition ${isFetchingLocation || isSavingAddress
                ? 'cursor-not-allowed bg-gray-400'
                : 'bg-orange-hover hover:bg-orange-600'
              }`}
          >
            {t('cart.delivery_modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
};


const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const isAuthenticated = authService.isAuthenticated();
  const userId = currentUser?._id || currentUser?.id;

  useEffect(() => {
    if (!isAuthenticated) preloadRecaptcha();
  }, [isAuthenticated]);

  const {
    openKkiapayWidget,
    addKkiapayListener,
    removeKkiapayListener,
  } = useKKiaPay();

  /** Évite les doubles créations de commande si le widget émet plusieurs fois `success`. */
  const kkiapayOrderLockRef = useRef(false);
  const handleCreateOrderRef = useRef(async () => {});

  const { items: cartItems, clearCart, addItem, removeItem, refetchCart } = useCart();
  const { removeItemFromAPI } = useCartOperations();

  // Supprime un item du panier (local + serveur si user connecté avec itemId).
  // Pattern identique à celui utilisé sur la page /cart.
  const handleRemoveItem = (item) => {
    if (!item) return;
    if (item.itemId) {
      removeItemFromAPI(
        item.itemId,
        () => removeItem(item.productId, item.variantId, item.optionKey),
        () => toast.error(t('cart.errors.remove_error', "Erreur lors de la suppression du produit.")),
      );
    } else {
      removeItem(item.productId, item.variantId, item.optionKey);
    }
  };

  // Synchroniser le panier avec l'API
  const { cartData, isLoading: isLoadingCart, error: cartError } = useCartSync({
    clearCart,
    addItem,
  });

  const onOrderSuccess = async (response) => {
    kkiapayOrderLockRef.current = false;

    // La gateway enveloppe la réponse : axios → response.data = { statusCode,
    // message, data: <order> }. Donc l'order réel (avec id, auth, etc.) se
    // trouve à response.data.data. On gère aussi les variantes possibles.
    const orderData = unwrapOrderResponse(response);

    // Auto-login : si la réponse contient un bundle `auth`, soit :
    //  - un nouveau compte vient d'être créé (premier achat avec compte),
    //  - soit l'utilisateur avait déjà un compte et son mot de passe a été validé.
    // Dans les deux cas on persiste le token et on met à jour la query
    // `current-user` pour authentifier immédiatement l'utilisateur.
    const auth = orderData?.auth;
    // On persiste via localStorage (équivalent du "Remember me").
    if (persistCheckoutAuthSession(auth)) {
      queryClient.setQueryData(['current-user'], auth.user);
      queryClient.invalidateQueries({ queryKey: ['current-user'] });

      toast.success(t('checkout.auto_login_success', 'Vous êtes maintenant connecté à votre compte.'));
    }

    // Vider panier local + cache React Query (+ API en secours si connecté)
    clearCart();
    const cartOwnerId =
      auth?.user?.id ||
      auth?.user?._id ||
      userId;
    if (cartOwnerId) {
      try {
        await cartAPI.clearCart(cartOwnerId);
      } catch {
        // Le backend vide déjà le panier à la création de commande ;
        // on ignore une erreur réseau ici pour ne pas bloquer le succès.
      }
      queryClient.setQueryData(getCartQueryKey(cartOwnerId), {
        data: { items: [] },
      });
      queryClient.invalidateQueries({ queryKey: getCartQueryKey(cartOwnerId) });
    } else {
      queryClient.removeQueries({ queryKey: [CART_QUERY_ROOT] });
    }
    if (typeof refetchCart === 'function') {
      refetchCart();
    }

    const orderId = orderData?._id || orderData?.id;
    navigate(`/checkout/success${orderId ? `?orderId=${orderId}` : ''}`);
  };
  const onOrderError = (error) => {
    kkiapayOrderLockRef.current = false;
    console.error('Erreur lors de la création de la commande:', error);
    toast.error(error.response?.data?.message || t('checkout.errors.create_order'));
  };

  const createOrderMutation = useMutation({
    mutationFn: (orderData) => ordersAPI.createOrder(orderData),
    onSuccess: onOrderSuccess,
    onError: onOrderError,
  });

  const createGuestOrderMutation = useMutation({
    mutationFn: (orderData) => ordersAPI.createGuestOrder(orderData),
    onSuccess: onOrderSuccess,
    onError: onOrderError,
  });

  // État pour les champs de formulaire
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    address: '',
    note: '',
    city: '',
    create_kilimmanjaro_account: false,
    password: '',
    confirmPassword: '',
  });

  // Si l'utilisateur est déjà connecté, on force le mode "sans création de compte"
  useEffect(() => {
    if (!isAuthenticated) return;
    setFormData((prev) => {
      if (!prev.create_kilimmanjaro_account && !prev.password && !prev.confirmPassword) return prev;
      return {
        ...prev,
        create_kilimmanjaro_account: false,
        password: '',
        confirmPassword: '',
      };
    });
  }, [isAuthenticated]);

  const [phoneNumber, setPhoneNumber] = useState('+228');
  const [fieldErrors, setFieldErrors] = useState({});
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('online'); // 'cash_on_delivery' | 'pickup_warehouse' | 'online'
  const billingFormRef = useRef(null);

  // Options de livraison : au moins "point de retrait" pour invités et connectés
  const [deliveryOptions, setDeliveryOptions] = useState(() => [
    // { id: 'pickup', label: 'Point de retrait', address: null, cost: 0 },
  ]);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState('pickup');

  // États pour le modal d'adresse de livraison
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [newAddressName, setNewAddressName] = useState('');
  const [newAddressFull, setNewAddressFull] = useState('');
  const [newAddressCoords, setNewAddressCoords] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [addressSaveError, setAddressSaveError] = useState('');
  const [isSavingAddress, setIsSavingAddress] = useState(false);

  // Adresses connecté : toutes les adresses utilisateur (+ entrepôt)
  const { data: deliveryAddressesData, isPending: isLoadingUserDeliveryAddresses } = useQuery({
    queryKey: ['delivery-addresses', userId],
    queryFn: () => addressService.getDeliveryAddresses(userId),
    enabled: isAuthenticated && !!userId,
    refetchOnMount: true,
  });

  // Invité : adresses plateforme avec isDefault = true
  const { data: defaultAddressesData, isPending: isLoadingDefaultAddresses } = useQuery({
    queryKey: ['delivery-addresses', 'defaults'],
    queryFn: () => addressService.getDefaultDeliveryAddresses(),
    enabled: !isAuthenticated,
    refetchOnMount: true,
  });

  const isLoadingDeliveryAddresses =
    (isAuthenticated && isLoadingUserDeliveryAddresses) ||
    (!isAuthenticated && isLoadingDefaultAddresses);

  const mapServerAddressesToOptions = (serverAddresses) =>
    serverAddresses.map((address) => ({
      id: String(address.id),
      label: address.name,
      address: address.address || '',
      cost: address.cost ?? null,
      isDefault: address.isDefault ?? address.is_default ?? false,
      latitude: address.latitude,
      longitude: address.longitude,
    }));

  useEffect(() => {
    const deliveryIdFromCart = searchParams.get('deliveryId');

    if (isAuthenticated && userId) {
      if (deliveryAddressesData === undefined) return;

      const raw = deliveryAddressesData?.data ?? deliveryAddressesData;
      const serverAddresses = Array.isArray(raw) ? raw : [];
      const mapped = mapServerAddressesToOptions(serverAddresses);

      setDeliveryOptions(mapped);

      if (mapped.length > 0) {
        const match =
          deliveryIdFromCart && mapped.find((m) => m.id === String(deliveryIdFromCart));
        if (match) {
          setSelectedDeliveryId(match.id);
        } else {
          const defaultAddress =
            serverAddresses.find((addr) => addr.isDefault || addr.is_default) ||
            serverAddresses[0];
          setSelectedDeliveryId(String(defaultAddress?.id ?? mapped[0].id));
        }
      } else {
        setSelectedDeliveryId('pickup');
      }
      return;
    }

    if (!isAuthenticated) {
      if (defaultAddressesData === undefined) return;

      const raw = defaultAddressesData?.data ?? defaultAddressesData;
      const serverAddresses = Array.isArray(raw) ? raw : [];
      const mapped = mapServerAddressesToOptions(serverAddresses);

      setDeliveryOptions(mapped);

      if (mapped.length > 0) {
        const match =
          deliveryIdFromCart && mapped.find((m) => m.id === String(deliveryIdFromCart));
        if (match) {
          setSelectedDeliveryId(match.id);
        } else {
          setSelectedDeliveryId(mapped[0].id);
        }
      } else {
        setSelectedDeliveryId('pickup');
      }
    }
  }, [
    deliveryAddressesData,
    defaultAddressesData,
    isAuthenticated,
    userId,
    searchParams,
  ]);

  // Préremplir le formulaire avec les données de l'utilisateur
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      firstName: currentUser.firstName ?? '',
      lastName: currentUser.lastName ?? '',
      company: currentUser.company || prev.company,
      email: currentUser.email || prev.email,
      address: currentUser.address || prev.address,
    }));

    if (currentUser.phone || currentUser.phoneNumber) {
      setPhoneNumber(currentUser.phone || currentUser.phoneNumber || '+228');
    }
  }, [currentUser]);

  // Calculs
  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + ((item?.price ?? 0) * (item?.quantity ?? 0)), 0);
  }, [cartItems]);

  const selectedDelivery = useMemo(
    () =>
      deliveryOptions.find((option) => option.id === selectedDeliveryId) ||
      deliveryOptions[0],
    [deliveryOptions, selectedDeliveryId]
  );

  const shippingCost = useMemo(() => {
    return selectedDelivery?.cost ?? 0;
  }, [selectedDelivery]);

  /** Adresse enregistrée « par défaut » sélectionnée → masquer toute la section paiement. */
  const hidePaymentSectionForDefaultAddress = useMemo(() => {
    if (!isAuthenticated || !userId) return false;
    const opt = deliveryOptions.find((o) => o.id === selectedDeliveryId);
    return Boolean(opt?.isDefault);
  }, [isAuthenticated, userId, deliveryOptions, selectedDeliveryId]);

  useEffect(() => {
    if (hidePaymentSectionForDefaultAddress) {
      setSelectedPaymentMethod('cash_on_delivery');
    }
  }, [hidePaymentSectionForDefaultAddress]);

  useEffect(() => {
    if (
      !hidePaymentSectionForDefaultAddress &&
      selectedPaymentMethod === 'pickup_warehouse'
    ) {
      setSelectedPaymentMethod('online');
    }
  }, [hidePaymentSectionForDefaultAddress, selectedPaymentMethod]);

  const total = computeCheckoutTotal(
    subtotal,
    appliedPromo?.discount,
    shippingCost,
  );

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) {
      setPromoError(t('checkout.errors.promo_required', 'Saisissez un code promo'));
      return;
    }
    if (!cartItems?.length) {
      setPromoError(t('checkout.errors.empty_cart'));
      return;
    }
    setPromoLoading(true);
    setPromoError('');
    try {
      const result = await promoService.validate({
        code,
        items: cartItems.map((item) => ({
          productId: String(item.productId),
          quantity: item.quantity || 1,
        })),
        userId: userId || undefined,
      });
      const payload = result?.data ?? result;
      if (!payload?.valid && !payload?.discount) {
        throw new Error(payload?.message || 'Code invalide');
      }
      setAppliedPromo({
        code: payload.code || code.toUpperCase(),
        discount: Number(payload.discount) || 0,
        shopId: payload.shopId || null,
        shopSubtotal: payload.shopSubtotal || 0,
      });
      toast.success(
        t('checkout.promo_code.applied', 'Code promo appliqué')
      );
    } catch (err) {
      setAppliedPromo(null);
      const data = err?.response?.data;
      const apiMessage =
        (typeof data === 'string' && data) ||
        data?.message ||
        (Array.isArray(data?.message) ? data.message.join(', ') : null);
      setPromoError(
        apiMessage ||
          err?.message ||
          t('checkout.errors.promo_invalid', 'Code promo invalide')
      );
    } finally {
      setPromoLoading(false);
    }
  };

  const clearPromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
    skipUrlPromoRef.current = true;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('promoCode');
        return next;
      },
      { replace: true },
    );
  };

  const cartSignature = useMemo(
    () =>
      (cartItems || [])
        .map((item) => `${item.productId}:${item.quantity || 1}`)
        .join('|'),
    [cartItems]
  );

  const skipUrlPromoRef = useRef(false);
  const attemptedUrlPromoRef = useRef(null);

  useEffect(() => {
    setAppliedPromo(null);
    setPromoError('');
    attemptedUrlPromoRef.current = null;
  }, [cartSignature]);

  // Reprend le code promo appliqué depuis /cart (?promoCode=…)
  useEffect(() => {
    const code = (searchParams.get('promoCode') || '').trim();
    const normalized = code.toUpperCase();
    if (!code || !cartItems?.length || appliedPromo || skipUrlPromoRef.current) {
      return;
    }
    if (attemptedUrlPromoRef.current === normalized) return;

    let cancelled = false;
    (async () => {
      setPromoInput(normalized);
      setPromoLoading(true);
      setPromoError('');
      try {
        const result = await promoService.validate({
          code,
          items: cartItems.map((item) => ({
            productId: String(item.productId),
            quantity: item.quantity || 1,
          })),
          userId: userId || undefined,
        });
        if (cancelled) return;
        const payload = result?.data ?? result;
        if (!payload?.valid && !payload?.discount) {
          throw new Error(payload?.message || 'Code invalide');
        }
        attemptedUrlPromoRef.current = normalized;
        setAppliedPromo({
          code: payload.code || normalized,
          discount: Number(payload.discount) || 0,
          shopId: payload.shopId || null,
          shopSubtotal: payload.shopSubtotal || 0,
        });
      } catch (err) {
        if (cancelled) return;
        attemptedUrlPromoRef.current = normalized;
        setAppliedPromo(null);
        const data = err?.response?.data;
        const apiMessage =
          (typeof data === 'string' && data) ||
          data?.message ||
          (Array.isArray(data?.message) ? data.message.join(', ') : null);
        setPromoError(
          apiMessage ||
            err?.message ||
            t('checkout.errors.promo_invalid', 'Code promo invalide'),
        );
      } finally {
        if (!cancelled) setPromoLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cartItems, searchParams, userId, t, appliedPromo]);

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    clearFieldError(name);
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'create_kilimmanjaro_account' ? checked : value,
    }));
  };

  const handlePhoneChange = (value) => {
    clearFieldError('phone');
    setPhoneNumber(value || '');
  };

  const validateBillingForm = () => {
    const errors = {};
    const firstName = (formData.firstName || '').trim();
    const lastName = (formData.lastName || '').trim();
    const email = (formData.email || '').trim();
    const phone = (phoneNumber || '').trim();

    if (!firstName) {
      errors.firstName = t(
        'checkout.errors.first_name_required',
        'Le prénom est obligatoire.'
      );
    }
    if (!lastName) {
      errors.lastName = t(
        'checkout.errors.last_name_required',
        'Le nom est obligatoire.'
      );
    }
    if (!email) {
      errors.email = t(
        'checkout.errors.email_required',
        "L'adresse e-mail est obligatoire."
      );
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t(
        'checkout.errors.email_invalid',
        'Veuillez saisir une adresse e-mail valide.'
      );
    }

    if (!phone || phone === '+228') {
      errors.phone = t(
        'checkout.errors.phone_required',
        'Le numéro de téléphone est obligatoire.'
      );
    } else if (!isValidPhoneNumber(phone)) {
      errors.phone = t(
        'checkout.errors.phone_invalid',
        'Veuillez saisir un numéro de téléphone valide.'
      );
    }

    if (!isAuthenticated) {
      const address = (formData.address || '').trim();
      if (!address) {
        errors.address = t(
          'checkout.errors.address_required',
          "L'adresse complète est obligatoire."
        );
      }
    }

    if (!isAuthenticated && formData.create_kilimmanjaro_account) {
      const pwd = (formData.password || '').trim();
      const confirmPwd = (formData.confirmPassword || '').trim();
      if (pwd.length < 6) {
        errors.password = t(
          'checkout.errors.password_min_length',
          'Le mot de passe doit contenir au moins 6 caractères.'
        );
      }
      if (!confirmPwd) {
        errors.confirmPassword = t(
          'checkout.errors.confirm_password_required',
          'Veuillez confirmer le mot de passe.'
        );
      } else if (pwd !== confirmPwd) {
        errors.confirmPassword = t(
          'checkout.errors.password_mismatch',
          'Les mots de passe ne correspondent pas.'
        );
      }
    }

    return errors;
  };

  const runBillingValidation = () => {
    const errors = validateBillingForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) return true;

    billingFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    toast.error(
      t(
        'checkout.errors.fill_required',
        'Veuillez corriger les champs indiqués ci-dessous.'
      )
    );
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleCreateOrder = async (orderOptions = {}) => {
    const { kkiapay } = orderOptions;
    const releaseKkiapayLock = () => {
      if (kkiapay) kkiapayOrderLockRef.current = false;
    };

    if (!cartItems || cartItems.length === 0) {
      releaseKkiapayLock();
      toast.error(t('checkout.errors.empty_cart'));
      return;
    }

    if (kkiapay && !kkiapay.transactionId) {
      releaseKkiapayLock();
      toast.error(t('checkout.errors.kkiapay_no_transaction'));
      return;
    }

    const kkiapayChannel = kkiapay ? extractKkiapayChannel(kkiapay) : null;
    const kkiapayAccount = kkiapay ? extractKkiapayAccount(kkiapay) : null;

    const kkiapayExtra =
      kkiapay && kkiapay.transactionId
        ? {
            paymentMethod: 'online_kkiapay',
            kkiapayTransactionId: kkiapay.transactionId,
            ...(kkiapay.flwRef != null && { kkiapayFlwRef: kkiapay.flwRef }),
            ...(kkiapay.partnerId != null && { kkiapayPartnerId: kkiapay.partnerId }),
            ...(kkiapayChannel && { paymentChannel: kkiapayChannel }),
            ...(kkiapayAccount && { paymentAccount: kkiapayAccount }),
          }
        : {
            paymentMethod:
              selectedPaymentMethod === 'online'
                ? 'online'
                : selectedPaymentMethod || 'cash_on_delivery',
          };

    const checkoutExtras = {
      ...(formData.company?.trim() && { company: formData.company.trim() }),
      ...(formData.note?.trim() && { note: formData.note.trim() }),
    };

    if (isAuthenticated && userId) {
      const cartId = cartData?.data?._id || cartData?.data?.id || cartData?._id || cartData?.id || userId;
      if (!cartId) {
        releaseKkiapayLock();
        toast.error(t('checkout.errors.no_cart_id'));
        return;
      }
      let adresseLivraisonId;
      if (selectedDeliveryId && selectedDeliveryId !== 'pickup') {
        adresseLivraisonId = selectedDeliveryId.toString();
      }
      createOrderMutation.mutate({
        cartId: String(cartId),
        ...(adresseLivraisonId ? { adresseLivraisonId } : {}),
        reduction: 0,
        ...(appliedPromo?.code ? { promoCode: appliedPromo.code } : {}),
        fraisLivraison: shippingCost,
        ...checkoutExtras,
        ...kkiapayExtra,
      });
      return;
    }

    // Commande invité : valider le formulaire et appeler POST /orders/guest
    if (!runBillingValidation()) {
      releaseKkiapayLock();
      return;
    }

    const email = (formData.email || '').trim();
    const firstName = (formData.firstName || '').trim();
    const lastName = (formData.lastName || '').trim();
    const phone = (phoneNumber || '').trim();
    const createAccount = !!formData.create_kilimmanjaro_account;

    let adresseLivraisonId;
    if (selectedDeliveryId && selectedDeliveryId !== 'pickup') {
      adresseLivraisonId = selectedDeliveryId.toString();
    }

    const guestOrderPayload = {
      items: cartItems.map((item) => ({
        productId: String(item.productId),
        quantity: item.quantity || 1,
      })),
      firstName,
      lastName,
      company: (formData.company || '').trim() || undefined,
      email,
      phone,
      city: (formData.city || '').trim() || '',
      address: (formData.address || '').trim(),
      createAccount,
      password: createAccount ? (formData.password || '').trim() : '',
      confirmPassword: createAccount ? (formData.confirmPassword || '').trim() : '',
      reduction: 0,
      ...(appliedPromo?.code ? { promoCode: appliedPromo.code } : {}),
      fraisLivraison: shippingCost,
      ...(adresseLivraisonId ? { adresseLivraisonId } : {}),
      ...((formData.note || '').trim()
        ? { note: formData.note.trim() }
        : {}),
      ...kkiapayExtra,
    };

    let recaptchaToken;
    try {
      recaptchaToken = await getRecaptchaToken('guest_order');
    } catch (err) {
      releaseKkiapayLock();
      toast.error(
        err?.message?.includes('reCAPTCHA') || err?.message?.includes('Token')
          ? err.message
          : t(
              'checkout.errors.recaptcha',
              'Vérification anti-bot impossible. Réessayez.',
            ),
      );
      return;
    }

    createGuestOrderMutation.mutate({
      ...guestOrderPayload,
      recaptchaToken,
    });
  };

  handleCreateOrderRef.current = handleCreateOrder;

  useEffect(() => {
    if (isFedaPayProvider()) return undefined;

    const onSuccess = (data) => {
      if (kkiapayOrderLockRef.current) return;
      kkiapayOrderLockRef.current = true;
      handleCreateOrderRef.current({ kkiapay: data });
    };
    const onFailed = (err) => {
      kkiapayOrderLockRef.current = false;
      console.error('KKiaPay:', err);
      toast.error(t('checkout.errors.kkiapay_failed'));
    };

    addKkiapayListener('success', onSuccess);
    addKkiapayListener('failed', onFailed);
    return () => {
      removeKkiapayListener('success');
      removeKkiapayListener('failed');
    };
  }, [addKkiapayListener, removeKkiapayListener, t]);

  // Fonctions pour gérer le modal d'adresse de livraison
  const handleAddAddressClick = () => {
    if (isFetchingLocation) return;

    setIsAddingAddress(true);
    setIsFetchingLocation(true);
    setLocationError('');
    setAddressSaveError('');
    setNewAddressCoords(null);
    setNewAddressName('');
    setNewAddressFull('');

    if (!navigator.geolocation) {
      setLocationError(t('cart.delivery_modal.errors.geolocation_not_supported'));
      setIsFetchingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setNewAddressCoords({ latitude, longitude });

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            { headers: { 'User-Agent': 'kilimmanjaro-app/1.0' } }
          );

          if (!response.ok) {
            throw new Error(t('cart.delivery_modal.errors.cannot_get_place'));
          }

          const data = await response.json();
          const displayName = data?.display_name || t('cart.delivery_modal.custom_address');
          setNewAddressName(displayName);
          setNewAddressFull(displayName);
          setLocationError('');
        } catch (error) {
          console.error(error);
          setLocationError(t('cart.delivery_modal.errors.cannot_get_location_name'));
          setAddressSaveError('');
          const fallback = t('account.addresses.new_address');
          setNewAddressName(fallback);
          setNewAddressFull(fallback);
        } finally {
          setIsFetchingLocation(false);
        }
      },
      (error) => {
        console.error(error);
        setLocationError(t('cart.delivery_modal.errors.cannot_get_location'));
        setAddressSaveError('');
        const fallback = t('account.addresses.new_address');
        setNewAddressName(fallback);
        setNewAddressFull(fallback);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleCreateAddress = async () => {
    if (!newAddressName.trim()) {
      setAddressSaveError(t('cart.delivery_modal.errors.name_required'));
      return;
    }

    if (!newAddressFull.trim()) {
      setAddressSaveError(t('cart.delivery_modal.errors.address_required'));
      return;
    }

    if (!userId) {
      setAddressSaveError(t('cart.delivery_modal.errors.login_required'));
      return;
    }

    setIsSavingAddress(true);
    setAddressSaveError('');

    try {
      const payload = {
        name: newAddressName.trim(),
        address: newAddressFull.trim(),
        latitude: newAddressCoords?.latitude ?? null,
        longitude: newAddressCoords?.longitude ?? null,
        isDefault: false,
      };

      const rawResponse = await addressService.createDeliveryAddress(
        userId,
        payload
      );

      const createdAddress = rawResponse || {};
      const newId = createdAddress.id;

      setSelectedDeliveryId(newId);
      await queryClient.invalidateQueries({ queryKey: ['delivery-addresses', userId] });
      setIsAddingAddress(false);
      setNewAddressName('');
      setNewAddressFull('');
      setNewAddressCoords(null);
    } catch (error) {
      console.error(error);
      const apiMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message;
      setAddressSaveError(
        apiMessage || t('cart.delivery_modal.errors.cannot_save')
      );
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleSaveDelivery = () => {
    setIsDeliveryModalOpen(false);
    setIsAddingAddress(false);
    setIsFetchingLocation(false);
    setLocationError('');
    setAddressSaveError('');
    setNewAddressFull('');
  };

  const handleCloseModal = () => {
    setIsDeliveryModalOpen(false);
    setIsAddingAddress(false);
    setIsFetchingLocation(false);
    setLocationError('');
    setAddressSaveError('');
    setNewAddressFull('');
  };


  const openKkiaPay = () => {
    if (!kkiapayPublicKey) {
      toast.error(t('checkout.errors.kkiapay_missing_key'));
      return;
    }
    const email =
      (formData.email || '').trim() || currentUser?.email || '';
    const phoneDigits = (phoneNumber || '').replace(/\D/g, '');
    // eslint-disable-next-line no-unused-vars
    const phone =
      phoneDigits.length >= 8 ? phoneDigits.slice(-12) : '';
    const fullname = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();

    openKkiapayWidget({
      amount: Math.round(Number(total) || 0),
      api_key: kkiapayPublicKey,
      sandbox: kkiapaySandbox,
      email: email || '',
      phone,
      // phone: '61000001',
      ...(fullname ? { fullname } : {}),
    });
  };

  const openFedaPay = async () => {
    if (!fedapayPublicKey) {
      toast.error(t('checkout.errors.fedapay_missing_key'));
      return;
    }

    const email =
      (formData.email || '').trim() || currentUser?.email || '';
    const firstName = (formData.firstName || '').trim() || currentUser?.firstName || '';
    const lastName = (formData.lastName || '').trim() || currentUser?.lastName || '';

    try {
      const transaction = await openFedaPayCheckout({
        amount: total,
        description: t('checkout.fedapay.transaction_description', {
          defaultValue: 'Commande Kilimmanjaro',
        }),
        email,
        firstName,
        lastName,
        phone: phoneNumber,
      });

      if (!transaction) {
        return;
      }

      // Étape UI : confirmation visuelle uniquement (commande + webhook au prochain sprint).
      toast.success(t('checkout.fedapay.success_preview'));
      console.info('FedaPay transaction:', transaction);
    } catch (err) {
      console.error('FedaPay:', err);
      if (err?.message === 'FEDAPAY_PUBLIC_KEY_MISSING') {
        toast.error(t('checkout.errors.fedapay_missing_key'));
        return;
      }
      toast.error(t('checkout.errors.fedapay_failed'));
    }
  };

  const handlePlaceOrder = () => {
    // Garde-fou : si un produit est en rupture, on refuse de soumettre.
    // Le bouton est déjà disabled, c'est une sécurité supplémentaire.
    if (cartItems.some((it) => it.isUnavailable)) {
      toast.error(
        t(
          'checkout.errors.unavailable_items',
          "Votre panier contient des produits en rupture de stock. Retirez-les avant de commander."
        )
      );
      return;
    }

    if (!runBillingValidation()) return;

    if (
      !hidePaymentSectionForDefaultAddress &&
      selectedPaymentMethod === 'online'
    ) {
      if (!cartItems?.length) {
        toast.error(t('checkout.errors.empty_cart'));
        return;
      }
      if (total <= 0) {
        toast.error(
          t('checkout.errors.total_zero') ||
            "Le montant total à payer est de 0 FCFA. Le paiement en ligne n'est pas possible."
        );
        return;
      }
      if (isFedaPayProvider()) {
        openFedaPay();
      } else {
        openKkiaPay();
      }
      return;
    }
    handleCreateOrder();
  };

  const cartUrl = useMemo(() => {
    if (selectedDeliveryId && selectedDeliveryId !== 'pickup') {
      return `/cart?deliveryId=${encodeURIComponent(selectedDeliveryId)}`;
    }
    return '/cart';
  }, [selectedDeliveryId]);

  return (
    <ClientLayout>
      <div className="flex-1 px-4 sm:px-6 md:px-0 md:mx-8 lg:mx-16 mx-auto py-8">
        {/* Fil d'Ariane */}
        <div className="mb-8">
          <nav className="text-sm text-gray-600">
            <Link to="/" className="hover:text-gray-900">{t('checkout.breadcrumb.home')}</Link>
            <span className="mx-2">&gt;</span>
            <Link to={cartUrl} className="hover:text-gray-900">{t('checkout.breadcrumb.cart')}</Link>
            <span className="mx-2">&gt;</span>
            <span className="text-gray-900">{t('checkout.breadcrumb.checkout')}</span>
          </nav>
        </div>

        {/* Section Code Promo — même UI que /cart */}
        <div className="mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
              <h2 className="text-sm sm:text-base font-medium text-gray-900 shrink-0">
                {t('cart.coupon.label')}
              </h2>
              <input
                type="text"
                placeholder={t('cart.coupon.placeholder')}
                value={promoInput}
                onChange={(e) => {
                  setPromoInput(e.target.value.toUpperCase());
                  setPromoError('');
                }}
                disabled={!!appliedPromo}
                className="w-full flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-orange-light focus:border-transparent text-sm disabled:bg-gray-100"
              />
              {appliedPromo ? (
                <button
                  type="button"
                  onClick={clearPromo}
                  className="w-full sm:w-auto whitespace-nowrap px-6 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-100 transition-colors text-sm"
                >
                  {t('checkout.promo_code.remove', 'Retirer')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={promoLoading}
                  className="w-full sm:w-auto whitespace-nowrap px-6 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors text-sm disabled:opacity-50"
                >
                  {promoLoading
                    ? t('checkout.promo_code.applying', 'Vérification…')
                    : t('cart.coupon.apply')}
                </button>
              )}
            </div>
            {promoError ? (
              <p className="mt-2 text-sm text-red-600">{promoError}</p>
            ) : null}
            {appliedPromo ? (
              <p className="mt-2 text-sm text-green-700">
                {t('checkout.promo_code.active', 'Code')}{' '}
                <strong>{appliedPromo.code}</strong>: −{' '}
                {Number(appliedPromo.discount).toLocaleString('fr-FR')} Fcfa
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Section Détails de facturation - 2/3 de la largeur */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg">
              <h2 className="font-bold text-gray-900 mb-6">{t('checkout.billing.title')}</h2>

              {isAuthenticated && isLoadingUser ? (
                <div className="p-12 text-center">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-hover"></div>
                    <p className="text-gray-500">{t('checkout.billing.loading')}</p>
                  </div>
                </div>
              ) : (
                <form
                  ref={billingFormRef}
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  noValidate
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                    {/* Prénom */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.billing.labels.first_name')}
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder={t('checkout.billing.placeholders.first_name')}
                        aria-invalid={!!fieldErrors.firstName}
                        className={`w-full px-4 py-3 border rounded-lg bg-gray-50 focus:outline-none ${
                          fieldErrors.firstName
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      {fieldErrors.firstName && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.firstName}</p>
                      )}
                    </div>

                    {/* Nom de famille */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.billing.labels.last_name')}
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder={t('checkout.billing.placeholders.last_name')}
                        aria-invalid={!!fieldErrors.lastName}
                        className={`w-full px-4 py-3 border rounded-lg bg-gray-50 focus:outline-none ${
                          fieldErrors.lastName
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      {fieldErrors.lastName && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.lastName}</p>
                      )}
                    </div>

                    {/* Nom de l'entreprise */}
                    <div className='col-span-2'>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.billing.labels.company')}
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder={t('checkout.billing.placeholders.company')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.billing.labels.email')}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('checkout.billing.placeholders.email')}
                        title={t('account.personal.email')}
                        readOnly={isAuthenticated}
                        aria-invalid={!!fieldErrors.email}
                        className={`w-full px-4 py-3 border rounded-lg bg-gray-50 focus:outline-none ${
                          fieldErrors.email
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      {fieldErrors.email && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                      )}
                    </div>

                    {/* Téléphone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.billing.labels.phone')}
                      </label>
                      <PhoneInput
                        international
                        defaultCountry="TG"
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        placeholder={t('checkout.billing.placeholders.phone')}
                        className={`w-full px-4 py-1.5 pr-0 border rounded-lg bg-gray-50 focus:border-transparent ${
                          fieldErrors.phone
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      {fieldErrors.phone && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>
                      )}
                    </div>

                    {/* Ville */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.billing.labels.city') || 'Ville'}
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder={t('checkout.billing.placeholders.city') || 'Ville'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
                      />
                    </div>

                    {/* Adresse complète — obligatoire pour les invités */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isAuthenticated
                          ? t('checkout.billing.labels.address')
                          : t('checkout.billing.labels.address_required', 'Adresse complète*')}
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder={t('checkout.billing.placeholders.address')}
                        aria-invalid={!!fieldErrors.address}
                        className={`w-full px-4 py-3 border rounded-lg bg-gray-50 focus:outline-none ${
                          fieldErrors.address
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      {fieldErrors.address && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.address}</p>
                      )}
                    </div>

                    {/* Note de commande */}
                    <div className='col-span-2'>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.billing.labels.order_note')}
                      </label>
                      <textarea
                        name="note"
                        value={formData.note}
                        onChange={handleInputChange}
                        rows={2}
                        placeholder={t('checkout.billing.placeholders.order_note')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
                      />
                    </div>

                    {/* Créer un compte (invité uniquement) */}
                    {!isAuthenticated ? (
                      <>
                        <div className='col-span-2'>
                          <input
                            type="checkbox"
                            name="create_kilimmanjaro_account"
                            id="create_kilimmanjaro_account"
                            checked={!!formData.create_kilimmanjaro_account}
                            onChange={handleInputChange}
                            className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                          <label htmlFor="create_kilimmanjaro_account" className='text-sm font-medium text-gray-700 ml-2'>
                            {t('checkout.billing.create_account') || 'Créer un compte'}
                          </label>
                        </div>

                        {formData.create_kilimmanjaro_account && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('checkout.billing.labels.password') || 'Mot de passe'}
                              </label>
                              <div className="relative">
                                <input
                                  type={showPassword ? 'text' : 'password'}
                                  name="password"
                                  value={formData.password}
                                  onChange={handleInputChange}
                                  placeholder="••••••••"
                                  minLength={6}
                                  autoComplete="new-password"
                                  aria-invalid={!!fieldErrors.password}
                                  className={`w-full px-4 py-3 pr-12 border rounded-lg bg-gray-50 focus:outline-none ${
                                    fieldErrors.password
                                      ? 'border-red-500'
                                      : 'border-gray-300'
                                  }`}
                                />
                                <button
                                  type="button"
                                  tabIndex={-1}
                                  onClick={() => setShowPassword((v) => !v)}
                                  aria-label={
                                    showPassword
                                      ? t('checkout.billing.labels.hide_password', 'Masquer le mot de passe')
                                      : t('checkout.billing.labels.show_password', 'Afficher le mot de passe')
                                  }
                                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 bg-transparent border-0 shadow-none outline-none focus:outline-none focus:ring-0"
                                >
                                  {showPassword ? (
                                    <EyeOff className="w-5 h-5" />
                                  ) : (
                                    <Eye className="w-5 h-5" />
                                  )}
                                </button>
                              </div>
                              {fieldErrors.password && (
                                <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('checkout.billing.labels.confirm_password') || 'Confirmer le mot de passe'}
                              </label>
                              <div className="relative">
                                <input
                                  type={showConfirmPassword ? 'text' : 'password'}
                                  name="confirmPassword"
                                  value={formData.confirmPassword}
                                  onChange={handleInputChange}
                                  placeholder="••••••••"
                                  minLength={6}
                                  autoComplete="new-password"
                                  aria-invalid={!!fieldErrors.confirmPassword}
                                  className={`w-full px-4 py-3 pr-12 border rounded-lg bg-gray-50 focus:outline-none ${
                                    fieldErrors.confirmPassword
                                      ? 'border-red-500'
                                      : 'border-gray-300'
                                  }`}
                                />
                                <button
                                  type="button"
                                  tabIndex={-1}
                                  onClick={() => setShowConfirmPassword((v) => !v)}
                                  aria-label={
                                    showConfirmPassword
                                      ? t('checkout.billing.labels.hide_confirm_password', 'Masquer la confirmation')
                                      : t('checkout.billing.labels.show_confirm_password', 'Afficher la confirmation')
                                  }
                                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 bg-transparent border-0 shadow-none outline-none focus:outline-none focus:ring-0"
                                >
                                  {showConfirmPassword ? (
                                    <EyeOff className="w-5 h-5" />
                                  ) : (
                                    <Eye className="w-5 h-5" />
                                  )}
                                </button>
                              </div>
                              {fieldErrors.confirmPassword && (
                                <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    ) : null}
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Section Totaux - 1/3 de la largeur */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6 sticky top-8">
              <h2 className="text-lg font-bold text-gray-900 mb-6">{t('checkout.totals.title')}</h2>

              {/* Liste des produits du panier */}
              <div className="mb-6 pb-4 border-b border-gray-300">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('checkout.totals.products')}</h3>
                {isLoadingCart ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                    <span className="ml-2 text-sm text-gray-600">{t('checkout.totals.loading')}</span>
                  </div>
                ) : cartError ? (
                  <p className="text-sm text-red-500">{t('checkout.totals.error')}</p>
                ) : cartItems.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('checkout.totals.empty')}</p>
                ) : (
                  <div className="space-y-2">
                    {cartItems.map((item) => {
                      const isUnavailable = !!item.isUnavailable;
                      return (
                        <div
                          key={`${item.productId}-${item.variantId ?? 'default'}-${item.optionKey ?? 'base'}`}
                          className={`text-sm ${isUnavailable ? 'opacity-60' : ''}`}
                        >
                          <div className="flex justify-between items-center gap-2">
                            <span
                              className={`flex-1 pr-2 ${
                                isUnavailable
                                  ? 'text-gray-500 line-through'
                                  : 'text-gray-900'
                              }`}
                            >
                              {item.name} x {item.quantity}
                            </span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span
                                className={`font-medium whitespace-nowrap ${
                                  isUnavailable
                                    ? 'text-gray-400 line-through'
                                    : 'text-gray-900'
                                }`}
                              >
                                {((item?.price ?? 0) * (item?.quantity ?? 0)).toLocaleString()} Fcfa
                              </span>
                              {/* Bouton corbeille (même style que /cart) — visible
                                  uniquement pour les produits en rupture */}
                              {isUnavailable && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveItem(item)}
                                  aria-label={t(
                                    'checkout.totals.remove_unavailable',
                                    'Retirer ce produit du panier',
                                  )}
                                  title={t(
                                    'checkout.totals.remove_unavailable',
                                    'Retirer ce produit du panier',
                                  )}
                                  className="text-red-500 hover:text-red-700 p-2 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 shrink-0"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                          {isUnavailable && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                              {t('checkout.totals.out_of_stock', 'En rupture de stock')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Alerte globale si au moins un produit est en rupture */}
                {cartItems.some((it) => it.isUnavailable) && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {t(
                      'checkout.totals.has_unavailable',
                      "Certains produits de votre panier sont en rupture de stock. Retirez-les du panier pour finaliser la commande."
                    )}
                  </div>
                )}
              </div>

              {/* Sous-total */}
              <div className="mb-6 pb-4 border-b border-gray-300">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{t('checkout.totals.subtotal')}</span>
                  <span className="text-gray-900 font-medium">{subtotal.toLocaleString()} Fcfa</span>
                </div>
                {appliedPromo?.discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-700">
                      {t('checkout.totals.discount', 'Réduction')} ({appliedPromo.code})
                    </span>
                    <span className="text-green-700 font-medium">
                      −{Number(appliedPromo.discount).toLocaleString()} Fcfa
                    </span>
                  </div>
                )}
              </div>

              {/* Adresses de livraison */}
              <div className="mb-6 flex justify-between items-center">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  {t('checkout.totals.delivery_address')}
                </h3>
                {!isAuthenticated && isLoadingDeliveryAddresses ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-gray-600">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                    {t('checkout.totals.loading_addresses')}
                  </div>
                ) : !isAuthenticated && deliveryOptions.length === 0 ? (
                  <p className="text-sm text-gray-600">
                    {t('checkout.totals.guest_delivery_hint')}
                  </p>
                ) : isAuthenticated && isLoadingDeliveryAddresses ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-gray-600">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                    {t('checkout.totals.loading_addresses')}
                  </div>
                ) : deliveryOptions.length === 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      {t('checkout.totals.no_saved_addresses')}
                    </p>
                    <button
                      type="button"
                      onClick={() => setIsDeliveryModalOpen(true)}
                      className="text-left text-sm font-medium text-orange-500 transition hover:text-orange-600"
                    >
                      {t('cart.delivery_modal.add_address')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {deliveryOptions.map((option) => {
                      const isActive = option.id === selectedDeliveryId;

                      return (
                        <div key={option.id} className="flex justify-end items-center gap-2 py-1 text-sm">

                          {/* Label cliquable qui sélectionne le radio */}
                          <label
                            htmlFor={`delivery-${option.id}`}
                            className={`cursor-pointer text-xs ${isActive ? 'font-semibold text-orange-00' : 'font-medium text-gray-600'
                              }`}
                          >
                            {option.label}
                          </label>

                          {option.isDefault && option.latitude && option.longitude ? (
                            <button
                              type="button"
                              onClick={() => {
                                const lat = Number(option.latitude);
                                const lng = Number(option.longitude);
                                window.open(
                                  `https://www.google.com/maps?q=${lat},${lng}`,
                                  '_blank',
                                  'noopener,noreferrer'
                                );
                              }}
                              className="text-blue-600 underline underline-offset-2 hover:text-blue-700 text-xs"
                              title="Voir sur la carte"
                            >
                              (Voir sur le map)
                            </button>
                          ) : null}

                          <input
                            id={`delivery-${option.id}`}
                            type="radio"
                            name="checkout-delivery"
                            value={option.id}
                            checked={isActive}
                            onChange={() => setSelectedDeliveryId(option.id)}
                            className="mt-1 h-4 w-4 shrink-0 border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        </div>
                      );
                    })}
                    {isAuthenticated && (
                      <div className="flex justify-end mt-8">
                        <button
                          type="button"
                          onClick={() => setIsDeliveryModalOpen(true)}
                          className="text-left text-sm font-medium text-orange-500 transition hover:text-orange-600 mt-6"
                        >
                          {t('cart.delivery_modal.add_address')}
                        </button>
                      </div>
                    )}

                  </div>
                )}
              </div>

              {/* Total */}
              <div className="border-y border-gray-300 py-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-900">{t('checkout.totals.total')}</span>
                  <span className="text-lg font-bold text-gray-900">{total.toLocaleString()} Fcfa</span>
                </div>
              </div>

              {/* Méthodes de paiement (masqué si adresse par défaut ; sinon sans « paiement entrepôt ») */}
              {!hidePaymentSectionForDefaultAddress && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    {t('checkout.totals.payment_method')}
                  </h3>
                  <div className="space-y-2">
                    <label
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition ${
                        selectedPaymentMethod === 'online'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value="online"
                        checked={selectedPaymentMethod === 'online'}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        className="h-4 w-4 border-gray-300 text-red-500 focus:ring-red-500"
                      />
                      <span className="text-sm font-medium text-gray-900">
                        {t('checkout.totals.online')}
                      </span>
                    </label>

                    <label
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition ${
                        selectedPaymentMethod === 'cash_on_delivery'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value="cash_on_delivery"
                        checked={selectedPaymentMethod === 'cash_on_delivery'}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        className="h-4 w-4 border-gray-300 text-red-500 focus:ring-red-500"
                      />
                      <span className="text-sm font-medium text-gray-900">
                        {t('checkout.totals.cash_on_delivery')}
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Message d'avertissement quand le total est égal à 0 */}
              {total <= 0 && cartItems.length > 0 && (
                <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                  <p className="font-semibold">
                    {t('checkout.totals.total_zero_title') ||
                      'Montant à payer : 0 FCFA'}
                  </p>
                  <p className="mt-1 text-xs">
                    {t('checkout.totals.total_zero_message') ||
                      "Le paiement en ligne n'est pas disponible lorsque le total est nul. Veuillez vérifier votre panier ou choisir « Paiement à la livraison »."}
                  </p>
                </div>
              )}

              {/* Bouton passer à la commande */}
              {!isAuthenticated && (
                <RecaptchaLegalNotice className="mb-3 text-right" />
              )}
              <button
                type="button"
                onClick={handlePlaceOrder}
                disabled={
                  createOrderMutation.isPending ||
                  createGuestOrderMutation.isPending ||
                  isLoadingCart ||
                  cartItems.length === 0 ||
                  cartItems.some((it) => it.isUnavailable) ||
                  (selectedPaymentMethod === 'online' && total <= 0)
                }
                className="w-full bg-orange-hover text-white py-3 px-6 rounded-full hover:bg-orange-600 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {createOrderMutation.isPending || createGuestOrderMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('checkout.totals.creating')}
                  </span>
                ) : (
                  t('checkout.totals.place_order')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <DeliveryModeModal
        isOpen={isDeliveryModalOpen}
        onClose={handleCloseModal}
        options={deliveryOptions}
        selectedId={selectedDeliveryId}
        onSelect={setSelectedDeliveryId}
        onAddAddress={handleAddAddressClick}
        isAdding={isAddingAddress}
        isFetchingLocation={isFetchingLocation}
        newAddressName={newAddressName}
        onNewAddressNameChange={setNewAddressName}
        onCreateAddress={handleCreateAddress}
        isSavingAddress={isSavingAddress}
        onFinalize={handleSaveDelivery}
        locationError={locationError}
        addressSaveError={addressSaveError}
      />
    </ClientLayout>
  );
};

export default Checkout; 